// src/utils/consultVitals.js
//
// How the consultation presents a VitalsReading: which heading is honest about
// its date, and the chips with their server-computed flags. An old reading must
// never be shown as today's, so the heading says "Vitals today" only when the
// reading was measured on the appointment's own date.

import { VITAL_FLAG_LABELS } from './consultation';

const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/;
const pad = n => String(n).padStart(2, '0');

/** Local 'YYYY-MM-DD' of an API timestamp (zoned strings are converted to local time). */
export function localDay(value) {
  if (!value) return null;
  const s = String(value);
  if (!HAS_ZONE.test(s)) {
    const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : null;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 'HH:mm' of an API timestamp, or ''. */
export function localTime(value) {
  if (!value) return '';
  const s = String(value);
  if (!HAS_ZONE.test(s)) {
    const m = s.match(/T(\d{2}):(\d{2})/);
    return m ? `${m[1]}:${m[2]}` : '';
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const fmtShortDate = day => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * @returns {{kind: 'none'|'today'|'older'|'unknown', label: string}}
 *   today   → "Vitals today · 10:12"
 *   older   → "Last recorded · 12 Oct 2026"
 *   none    → "Vitals"  (nothing recorded)
 */
export function vitalsHeading(vitals, appointmentDate) {
  if (!vitals || !vitals.measuredAt) return { kind: 'none', label: 'Vitals' };
  const day = localDay(vitals.measuredAt);
  if (!day) return { kind: 'unknown', label: 'Last recorded' };
  const apptDay = localDay(appointmentDate);
  if (apptDay && day === apptDay) {
    const t = localTime(vitals.measuredAt);
    return { kind: 'today', label: t ? `Vitals today · ${t}` : 'Vitals today' };
  }
  return { kind: 'older', label: `Last recorded · ${fmtShortDate(day)}` };
}

// Which flag marks which reading, and in which direction.
const FLAG_FOR = {
  BP_HIGH: ['bp', '↑'], BP_LOW: ['bp', '↓'], SPO2_LOW: ['spo2', '↓'],
  FEVER: ['temp', '↑'], TACHYCARDIA: ['pulse', '↑'], BRADYCARDIA: ['pulse', '↓'],
};

/**
 * Chips for the glance tile: [{key, label, flagged, arrow, flagText}] where
 * `flagged` readings carry an arrow and the flag's words so colour is never the
 * only signal.
 */
export function vitalChips(v) {
  if (!v) return [];
  const flagByKey = {};
  (v.flags || []).forEach(f => { if (FLAG_FOR[f]) flagByKey[FLAG_FOR[f][0]] = f; });
  const chip = (key, text) => {
    const f = flagByKey[key];
    return {
      key, text, flagged: !!f, arrow: f ? FLAG_FOR[f][1] : '', flagText: f ? (VITAL_FLAG_LABELS[f] || f) : '',
    };
  };
  const out = [];
  if (v.systolic != null && v.diastolic != null) out.push(chip('bp', `BP ${v.systolic}/${v.diastolic}`));
  if (v.temperatureC != null) out.push(chip('temp', `Temp ${v.temperatureC}`));
  if (v.pulse != null) out.push(chip('pulse', `Pulse ${v.pulse}`));
  if (v.spo2 != null) out.push(chip('spo2', `SpO₂ ${v.spo2}%`));
  const body = [v.weightKg != null ? `${v.weightKg} kg` : null, v.bmi != null ? `BMI ${v.bmi}` : null].filter(Boolean);
  if (body.length) out.push({ key: 'body', text: body.join(' · '), flagged: false, arrow: '', flagText: '' });
  return out;
}

/** Flags the server sent that the chips cannot place (e.g. a future flag): shown as extra text. */
export function unplacedFlags(v) {
  return (v?.flags || []).filter(f => !FLAG_FOR[f]).map(f => VITAL_FLAG_LABELS[f] || f);
}
